import { esCuentaPremiumPerfil } from '../../../core/monetizacion/planes.js';
import { escapeHtml as esc } from '../../../core/ui/escapeHtml.js';

/** WhatsApp Mercado (AR): 11 6239-9695 */
const WHATSAPP_PREMIUM_E164 = '5491162399695';

/** Precios Plan Profesional (oferta vs lista). */
const PRECIO_PLAN_PRO_BASE_ARS = 49999;
const PRECIO_PLAN_PRO_OFERTA_ARS = 9999;

function fmtPrecioPlanArs(n) {
  const x = Math.max(0, Math.floor(Number(n) || 0));
  return x.toLocaleString('es-AR', { maximumFractionDigits: 0 });
}

function porcentajeDescuentoPlanPro() {
  const base = PRECIO_PLAN_PRO_BASE_ARS;
  const oferta = PRECIO_PLAN_PRO_OFERTA_ARS;
  if (!base || oferta >= base) return 0;
  return Math.round(((base - oferta) / base) * 100);
}

function iconBack() {
  return `<svg class="h-6 w-6 text-brand-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>`;
}

function iconRocket() {
  return `<svg class="h-7 w-7 shrink-0 text-white/90" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.281 5.119v4.8a6 6 0 007.281 5.119 14.98 14.98 0 0012.12-6.16 14.98 14.98 0 00-6.16-12.12z" /></svg>`;
}

function iconCheck() {
  return `<svg class="mt-0.5 h-5 w-5 shrink-0 text-brand-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>`;
}

export function buildPremiumWhatsAppUrl({ nombre, dni, email }) {
  const n =
    String(nombre || '').trim() ||
    String(email || '').split('@')[0] ||
    'vecino';
  const d = String(dni || '').trim() || 'pendiente';
  const text = `Hola soy "${n}" y mi dni es "${d}", quiero suscribirme al premium en lavictoria market.`;
  return `https://wa.me/${WHATSAPP_PREMIUM_E164}?text=${encodeURIComponent(text)}`;
}

/**
 * Pantalla Plan Profesional (pago): copy y CTA a WhatsApp con datos del perfil.
 */
export function createPremiumPlanPresenter({ render }) {
  function show({ onBack, profile, user } = {}) {
    if (esCuentaPremiumPerfil(profile)) {
      render(`
      <div class="min-h-screen bg-brand-surface mlv-bg-surface pb-10">
        <header class="sticky top-0 z-10 flex items-center gap-2 border-b border-slate-200/80 bg-brand-surface/95 px-2 py-2.5 backdrop-blur-sm">
          <button type="button" id="mlv-premium-back" class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-slate-200/60" aria-label="Volver">
            ${iconBack()}
          </button>
          <h1 class="min-w-0 flex-1 font-heading text-sm font-bold tracking-tight text-brand-primary sm:text-base">Plan Premium</h1>
        </header>
        <main class="mx-auto max-w-lg px-4 pt-6">
          <div class="rounded-2xl border border-emerald-200/80 bg-emerald-50/90 p-5 text-center shadow-sm">
            <p class="text-sm font-semibold text-emerald-900">Ya tenés un plan Premium activo en esta cuenta.</p>
            <p class="mt-2 text-xs leading-relaxed text-emerald-800/90">Los beneficios (tope de publicaciones, comisiones y sello si aplica) ya están habilitados según tu tipo de cuenta.</p>
          </div>
        </main>
      </div>
    `);
      document.getElementById('mlv-premium-back')?.addEventListener('click', () => onBack?.());
      return;
    }

    const waUrl = buildPremiumWhatsAppUrl({
      nombre: profile?.nombre,
      dni: profile?.dni,
      email: user?.email,
    });

    const benefits = [
      'Más visibilidad: hasta 15 publicaciones activas a la vez',
      'Sello de vendedor destacado',
      'Comisión nivel 5 (1 %) mientras dure tu plan',
      'Prioridad en destacados del barrio',
    ];

    const pctOff = porcentajeDescuentoPlanPro();
    const precioBaseFmt = fmtPrecioPlanArs(PRECIO_PLAN_PRO_BASE_ARS);
    const precioOfertaFmt = fmtPrecioPlanArs(PRECIO_PLAN_PRO_OFERTA_ARS);

    render(`
      <div class="min-h-screen bg-brand-surface mlv-bg-surface pb-10">
        <header class="sticky top-0 z-10 flex items-center gap-2 border-b border-slate-200/80 bg-brand-surface/95 px-2 py-2.5 backdrop-blur-sm">
          <button type="button" id="mlv-premium-back" class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-slate-200/60" aria-label="Volver">
            ${iconBack()}
          </button>
          <h1 class="min-w-0 flex-1 font-heading text-sm font-bold tracking-tight text-brand-primary sm:text-base">Planes Premium</h1>
          <span class="shrink-0 rounded-lg bg-slate-100 px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wide text-slate-600">lavictoria-market</span>
        </header>

        <main class="mx-auto max-w-lg px-4 pt-4 space-y-4">
          <section class="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-primary to-[#003d40] p-5 text-white shadow-lg">
            <div class="pointer-events-none absolute -right-2 -top-2 opacity-20">${iconRocket()}</div>
            <p class="inline-block rounded-full bg-brand-secondary/25 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-brand-secondary">Plan Profesional</p>
            <h2 class="font-heading mt-3 text-xl font-bold leading-tight">Impulsá tus ventas</h2>
            <p class="mt-2 text-sm leading-relaxed text-white/85">
              Llegá a más vecinos y convertite en el referente del barrio.
            </p>
          </section>

          <section class="rounded-3xl border border-slate-200/90 bg-white p-5 shadow-md ring-1 ring-slate-100">
            <h3 class="font-heading text-lg font-bold text-slate-900">Plan Profesional</h3>
            <p class="mt-2 text-[11px] font-bold uppercase tracking-wide text-amber-700">Oferta por tiempo limitado</p>
            <div class="mt-2 flex flex-wrap items-center gap-2">
              <span class="text-lg font-semibold text-slate-400 line-through decoration-slate-400 decoration-2" aria-label="Precio de lista">$ ${esc(precioBaseFmt)}</span>
              <span class="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-900 ring-1 ring-amber-200">Antes</span>
            </div>
            <div class="mt-2 flex flex-wrap items-baseline gap-3">
              <p class="font-heading text-3xl font-bold text-brand-primary sm:text-4xl">
                $ ${esc(precioOfertaFmt)} <span class="text-base font-semibold text-slate-600">al mes</span>
              </p>
              ${
                pctOff > 0
                  ? `<span class="mlv-discount-latido rounded-xl bg-brand-tertiary px-3 py-1.5 text-base font-black tabular-nums text-white shadow-md ring-2 ring-amber-400/50 sm:text-lg" aria-live="polite">−${pctOff}%</span>`
                  : ''
              }
            </div>
            ${
              pctOff > 0
                ? `<p class="mt-1 text-xs font-semibold text-brand-primary">${esc(String(pctOff))}% de descuento sobre el precio de lista</p>`
                : ''
            }
            <p class="mt-1 text-sm text-slate-500">Pago mensual sin permanencia</p>
            <ul class="mt-5 space-y-3 text-sm text-slate-800">
              ${benefits
                .map(
                  (b) => `
                <li class="flex gap-3">
                  ${iconCheck()}
                  <span>${esc(b)}</span>
                </li>`
                )
                .join('')}
            </ul>
            <a
              id="mlv-premium-wa"
              href="${waUrl}"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-primary py-3.5 text-sm font-bold text-white shadow-md transition hover:brightness-95"
            >
              Quiero el Plan Profesional
              <span aria-hidden="true">→</span>
            </a>
            <p class="mt-4 text-center text-[11px] leading-relaxed text-slate-500">
              Al suscribirte, aceptás los términos y condiciones de servicio de <span class="font-semibold text-slate-600">lavictoria-market</span>.
            </p>
          </section>

          <section class="flex items-center justify-center gap-3 rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-3 shadow-sm">
            <div class="flex -space-x-2">
              <span class="inline-flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-brand-muted text-[10px] font-bold text-brand-primary">A</span>
              <span class="inline-flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-slate-200 text-[10px] font-bold text-slate-600">B</span>
              <span class="inline-flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-brand-primary/15 text-[10px] font-bold text-brand-primary">C</span>
            </div>
            <div class="text-left text-xs text-slate-600">
              <p class="font-medium text-slate-700">Sumate a los vecinos que ya eligieron <strong class="text-brand-primary">Plan Profesional</strong></p>
            </div>
          </section>
        </main>
      </div>
    `);

    const back = () => onBack?.();
    document.getElementById('mlv-premium-back')?.addEventListener('click', back);
  }

  return { show };
}
